/**
 * Секция «Расписание» (программа дня) — отдельно от полноценных событий
 * (правка Артёма: «расписание отдельно от события, просто время и текст»).
 * Простые строки «время — текст». Менеджер добавляет/удаляет, гость читает.
 * Бэк: /v1/events/schedule (миграция 0066).
 */
import { useState } from 'react';
import { Button, Card } from '@hsc/ui';
import { useCreateScheduleItem, useDeleteScheduleItem, useSchedule } from './useEvents.js';

export interface ScheduleSectionProps {
  canManage: boolean;
  onError: (msg: string) => void;
  onInfo: (msg: string) => void;
}

export function ScheduleSection({ canManage, onError, onInfo }: ScheduleSectionProps): JSX.Element {
  const { data, isLoading, isError } = useSchedule();
  const create = useCreateScheduleItem();
  const remove = useDeleteScheduleItem();
  const [time, setTime] = useState('');
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);

  const items = data?.schedule ?? [];
  const busy = create.isPending || remove.isPending;

  const add = (): void => {
    if (time.trim() === '' || text.trim() === '') {
      onError('Укажите время и текст строки расписания.');
      return;
    }
    create.mutate(
      { time: time.trim(), text: text.trim(), sort_order: items.length },
      {
        onSuccess: () => {
          setTime('');
          setText('');
          setAdding(false);
          onInfo('Строка расписания добавлена.');
        },
        onError: (e) => onError(e.message || 'Не удалось добавить строку.'),
      },
    );
  };

  // Добавление строки расписания — ОТДЕЛЬНЫЙ экран (форма вместо списка), не
  // инлайн под списком (feedback_every_form_is_a_separate_screen).
  if (canManage && adding) {
    return (
      <section className="events-schedule" data-testid="events-schedule">
        <div className="ta-topbar">
          <h1 className="hsc-page__title">Строка расписания</h1>
        </div>
        <Card>
          <form
            className="hsc-stack"
            data-testid="schedule-add"
            onSubmit={(e) => {
              e.preventDefault();
              add();
            }}
          >
            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="schedule-time">Время</label>
              <input
                id="schedule-time"
                className="ta-input"
                type="text"
                maxLength={32}
                value={time}
                onChange={(e) => setTime(e.target.value)}
                placeholder="19:30"
                data-testid="schedule-time"
                autoFocus
              />
            </div>
            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="schedule-text">Текст</label>
              <input
                id="schedule-text"
                className="ta-input"
                type="text"
                maxLength={500}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Йога на террасе"
                data-testid="schedule-text"
              />
            </div>
            <div className="menu-form__submit">
              <Button type="submit" variant="primary" disabled={busy} data-testid="schedule-submit">
                {create.isPending ? 'Сохраняем…' : 'Добавить'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setTime('');
                  setText('');
                  setAdding(false);
                }}
                data-testid="schedule-back"
              >
                Назад
              </Button>
            </div>
          </form>
        </Card>
      </section>
    );
  }

  const del = (id: string): void => {
    remove.mutate(id, {
      onSuccess: () => onInfo('Строка расписания удалена.'),
      onError: (e) => onError(e.message || 'Не удалось удалить строку.'),
    });
  };

  return (
    <section className="events-schedule" data-testid="events-schedule">
      <h2 className="hsc-page__title events-schedule__title">Расписание дня</h2>
      <p className="ta-muted">Программа дня — простые строки «время — текст», отдельно от событий.</p>

      <Card>
        {isLoading ? (
          <p className="ta-muted" data-testid="schedule-loading">
            Загрузка расписания…
          </p>
        ) : isError ? (
          <p role="alert" data-testid="schedule-error">
            Не удалось загрузить расписание.
          </p>
        ) : items.length === 0 ? (
          <p className="ta-muted" data-testid="schedule-empty">
            {canManage
              ? 'Расписание пустое. Добавьте строки программы дня.'
              : 'Расписание на сегодня пока не опубликовано.'}
          </p>
        ) : (
          <ul className="events-schedule__list" data-testid="schedule-list">
            {items.map((it) => (
              <li key={it.id} className="events-schedule__row" data-testid={`schedule-row-${it.id}`}>
                <span className="events-schedule__time">{it.time}</span>
                <span className="events-schedule__text">{it.text}</span>
                {canManage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => del(it.id)}
                    data-testid={`schedule-del-${it.id}`}
                  >
                    Удалить
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {canManage && (
          <div className="events-schedule__add" data-testid="schedule-add-toolbar">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAdding(true)}
              data-testid="schedule-add-open"
            >
              + Строка расписания
            </Button>
          </div>
        )}
      </Card>
    </section>
  );
}

export default ScheduleSection;
